<script setup lang="ts">
  import { CalendarDate } from '@internationalized/date';
  import type { DateValue } from '@internationalized/date';
  import dayjs from 'dayjs';
  import { computed, onBeforeMount, ref, watch } from 'vue';

  import type { Invoice, Task } from '@stuut/api/models';
  import type { HttpError } from '@stuut/common/src/api_client';
  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { MonetaryText } from '@ui/components/monetary';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';

  import { stuut } from '@/api';
  import InvoiceListWidgetStatus from '@/components/common/invoice-list-widget/InvoiceStatus.vue';
  import PaymentPromiseSourceLink from '@/components/payment-promises/PaymentPromiseSourceLink.vue';

  interface Props {
    fetchingInvoiceId?: string | null;
    promiseData: Record<string, any>;
    task: Task;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'openSheet', invoiceId: string): void;
    (e: 'closeSheet'): void;
  }>();

  const isSaving = ref(false);
  const isDatePickerOpen = ref(false);
  const invoice = ref<Invoice>();
  const fetchingInvoice = ref(false);

  // const invoice = computed(
  //   () =>
  //     props.task?.invoices?.find(
  //       (invoice) => invoice.id === props.promiseData?.invoice_id,
  //     ) as Invoice,
  // );

  const peekFindInvoice = async (invoiceId: string) => {
    const record = props.task?.invoices?.find((inv) => inv.id === invoiceId);

    if (record) return record;

    try {
      fetchingInvoice.value = true;

      const invoice = await stuut.invoices.retrieve(invoiceId);
      return invoice || null;
    } finally {
      fetchingInvoice.value = false;
    }
  };

  const toCalendarDate = (dayjsDate: dayjs.Dayjs): CalendarDate => {
    return new CalendarDate(dayjsDate.year(), dayjsDate.month() + 1, dayjsDate.date());
  };

  const toCalendarString = (value?: string | Date | dayjs.Dayjs | null): string | null => {
    if (!value) return null;

    const date = dayjs(value);
    return date.isValid() ? toCalendarDate(date)?.toString() : null;
  };

  const promiseToPayDate = ref<DateValue | null>(
    invoice.value?.promised_to_pay_date
      ? toCalendarDate(dayjs(invoice.value?.promised_to_pay_date))
      : null,
  );

  const originalInvoiceDate = ref<string | null>(
    toCalendarString(invoice.value?.promised_to_pay_date),
  );

  const isDateMatchPromise = computed(() => {
    return originalInvoiceDate.value === props.promiseData?.promised_date;
  });

  const sourcePromise = computed(
    () => props.task?.payment_promise ?? invoice.value?.current_payment_promise ?? null,
  );

  const promiseAmount = computed(
    () => props.promiseData?.amount ?? sourcePromise.value?.amount ?? null,
  );
  const promiseCurrency = computed(
    () => props.promiseData?.currency ?? sourcePromise.value?.currency ?? invoice.value?.currency,
  );
  const promisePaymentType = computed(
    () => props.promiseData?.payment_type ?? sourcePromise.value?.payment_type ?? null,
  );

  const savePromisedPayment = async () => {
    isSaving.value = true;
    try {
      await stuut.invoices.update(
        invoice.value?.id!,
        {
          data: {
            promised_to_pay_date: promiseToPayDate.value?.toString() || null,
          },
        },
        invoice.value,
      );

      originalInvoiceDate.value = promiseToPayDate.value?.toString() || null;
      isDatePickerOpen.value = false;
      toast.success('Updated promised payment date');
    } catch (err) {
      toast.error((err as HttpError).message || 'An error occurred.');
    } finally {
      isSaving.value = false;
    }
  };

  watch(isDatePickerOpen, () => {
    promiseToPayDate.value = invoice.value?.promised_to_pay_date
      ? toCalendarDate(dayjs(invoice.value?.promised_to_pay_date))
      : null;
  });

  watch(
    () => invoice.value?.promised_to_pay_date,
    (newDate) => {
      originalInvoiceDate.value = toCalendarString(newDate);
    },
  );

  onBeforeMount(async () => {
    invoice.value = await peekFindInvoice(props.promiseData?.invoice_id);
  });
</script>

<template>
  <Button
    class="hover:underline cursor-pointer w-fit text-foreground text-xs gap-x-1.5"
    variant="link"
    size="inline"
    :disabled="fetchingInvoiceId === promiseData.invoice_id || fetchingInvoice"
    @click="emit('openSheet', promiseData.invoice_id)"
  >
    <span>{{ promiseData.invoice_number }}</span>

    <i
      v-if="fetchingInvoiceId === promiseData.invoice_id || fetchingInvoice"
      class="fa fa-spinner-third animate-spin text-2xs!"
    />
  </Button>

  <div class="flex items-center">
    <Timestamp
      v-if="invoice?.dueDateStartOfDay"
      :time="invoice.dueDateStartOfDay"
      format="MMM D YYYY"
      date-only
    />
    <span v-else class="text-muted-foreground">-</span>
  </div>

  <div class="flex items-center">
    <MonetaryText v-if="invoice" :value="invoice.amount_remaining" :currency="invoice.currency" />
    <span v-else class="text-muted-foreground">-</span>
  </div>

  <Popover v-model:open="isDatePickerOpen">
    <PopoverTrigger as-child>
      <Timestamp
        :time="invoice?.promised_to_pay_date"
        class="justify-start"
        :class="{
          'font-bold': !invoice?.promised_to_pay_date,
        }"
        fallback="Set Date"
        format="MMM D YYYY"
        date-only
        side="top"
        editable
      />
    </PopoverTrigger>

    <PopoverContent class="w-auto p-3 space-y-3">
      <!-- @vue-ignore -->
      <Calendar v-model="promiseToPayDate" calendar-label="Promise To Pay" />

      <div class="flex flex-row gap-x-2">
        <Button
          variant="outline"
          size="sm"
          class="flex-1"
          :loading="isSaving"
          :disabled="isSaving"
          @click="promiseToPayDate = null"
        >
          Clear
        </Button>

        <Button
          size="sm"
          class="flex-1"
          :loading="isSaving"
          :disabled="isSaving"
          @click="savePromisedPayment"
        >
          Save
        </Button>
      </div>
    </PopoverContent>
  </Popover>

  <MonetaryText
    v-if="promiseAmount"
    :value="promiseAmount"
    :currency="promiseCurrency"
    major-unit
  />
  <span v-else>-</span>

  <span class="capitalize">{{ promisePaymentType || '-' }}</span>

  <PaymentPromiseSourceLink v-if="sourcePromise" :promise="sourcePromise" variant="label" />
  <span v-else class="text-muted-foreground text-2xs">—</span>

  <div class="flex items-center">
    <InvoiceListWidgetStatus v-if="invoice" :invoice="invoice" />
  </div>
</template>

<style scoped></style>
